import { useTranslation } from "react-i18next";
import { useToast } from "../../modules/layout/components/ToastContext";
import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { IAuditLevel, IFilterGatewayLogModel, IGatewayLog, ILogType } from "../../interfaces/IUtilities";
import { getAll } from "../_request";
import {Field, Form, Formik} from "formik";
import Loader from "../../modules/layout/components/Loader";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import FiltersCard from "../../modules/layout/components/FiltersCard";
import { FormikDatePicker } from "../../modules/layout/components/FormikDatePicker";
import Pagination from "../../modules/layout/components/Pagination";
import { AuditLevelEnumText, LogTypeEnumText } from "../../helpers/EnumeratorHelper";
import SortDirection from "../../modules/layout/components/SortDirection";
import FormikSelect from '../../modules/layout/components/Select/FormikSelect';
import { formatDateTime, toApiDateTime } from "../../helpers/DateTimeHelper";

const GatewayLogList = () => {

    const {t} = useTranslation();
    const {showToast} = useToast();
    const navigate = useNavigate();

    const [auditLogs, setAuditLogs] = useState([] as IGatewayLog[]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [asc, setAsc] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sortField, setSortField] = useState("logId");
    const initialFormState = {
        logId: '',
        datefrom: '',
        dateto: '',
        correlationid: '',
        classname: '',
        userid: '',
        userName: '',
        fullName: '',
        // undefined means no filter; '' will mean filter for empty when explicitly selected
        logType: undefined as unknown as string | undefined,
        auditLevel: undefined as unknown as string | undefined
    }
    let order = asc ? "asc" : "desc";
    const [activeFilters, setActiveFilters] = useState<IFilterGatewayLogModel>(initialFormState);

    const handleFilterSearch = async (filters: IFilterGatewayLogModel) => {
        setPage(1);
        setActiveFilters(filters);
        await fetchLogs(filters);
    };

    useEffect(() => {
        fetchLogs();
    }, [navigate, showToast, page, sortField, asc, pageSize]);

    const logTypeOptions = useMemo(() => [
       
        { value: String(-1), label: '\u00A0' },
        { value: String(ILogType.DIS_REQUEST), label: LogTypeEnumText[ILogType.DIS_REQUEST] },
        { value: String(ILogType.DIS_RESPONSE), label: LogTypeEnumText[ILogType.DIS_RESPONSE] },
        { value: String(ILogType.SERVICE_REQUEST), label: LogTypeEnumText[ILogType.SERVICE_REQUEST] },
        { value: String(ILogType.SERVICE_RESPONSE), label: LogTypeEnumText[ILogType.SERVICE_RESPONSE] },
        { value: String(ILogType.DIS_EXCEPTION), label: LogTypeEnumText[ILogType.DIS_EXCEPTION] },
    ], []);

    const auditLevelOptions = useMemo(() => [
  
        { value: String(-1), label: '\u00A0' },
        { value: String(IAuditLevel.AuditLevel1), label: AuditLevelEnumText[IAuditLevel.AuditLevel1] },
        { value: String(IAuditLevel.AuditLevel2), label: AuditLevelEnumText[IAuditLevel.AuditLevel2] },
    ], []);

    const fetchLogs = async (filters: IFilterGatewayLogModel = activeFilters) => {
        try {

            const params = [
                { key: 'page', value: page },
                { key: 'pageSize', value: pageSize },
                { key: 'sortBy', value: sortField },
                { key: 'order', value: order },
            ];
            //if (filters.logId) params.push({ key: 'logId', value: filters.logId });

            if (filters.datefrom)
                params.push({key: 'datefrom', value: toApiDateTime(filters.datefrom)!});

            if (filters.dateto)
                params.push({key: 'dateto', value: toApiDateTime(filters.dateto, true)!});

            if (filters.classname) params.push({ key: 'classname', value: filters.classname });
            //if (filters.correlationid) params.push({ key: 'correlationid', value: filters.correlationid });
            if (filters.correlationid) {
                const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
                
                if (guidRegex.test(String(filters.correlationid))) {
                    params.push({ key: 'correlationid', value: filters.correlationid });
                } else {
                    showToast(t("CorrelationIdInvalidFormat"), 'warning');
                    setLoading(false);
                    return; 
                }
            }

            if (filters.userid) params.push({ key: 'userid', value: filters.userid });
            if (filters.userName) params.push({ key: 'userName', value: filters.userName });
            if (filters.fullName) params.push({ key: 'fullName', value: filters.fullName });
            // include empty-string selections, but skip when value is null/undefined (no filter)
            if (filters.logType !== undefined && filters.logType !== null)
                params.push({ key: 'logType', value: filters.logType });
            if (filters.auditLevel !== undefined && filters.auditLevel !== null)
                params.push({ key: 'auditLevel', value: filters.auditLevel });

            let result = await getAll('/api/gatewaylogs/getall/',
                '?' + params.map(f => `${encodeURIComponent(f.key)}=${encodeURIComponent(f.value as string)}`).join('&'));

            let dataResults = result?.data as any;
            dataResults = dataResults["items"] as IGatewayLog[];
            setTotal(result?.data.totalCount);
            setTotalPages(Math.ceil((result?.data.totalCount ?? 0) / result?.data.pageSize));

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setAuditLogs(dataResults);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                showToast(t('StatusCode', {code: String(respStatus)}), 'error');
                throw new Error(t('StatusCode', { code: String(respStatus)} ));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate('/');
        }
    }

    const handleSort = (field: string) => {
        if (sortField === field) {
            setAsc(!asc);
        } else {
            setSortField(field);
            setAsc(true);
        }
    };

    const truncateText = (text: string, maxLength: number): string => {
        if (!text) return "";
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };


    return (
        <div className="gg-gatewayLogList container mt-4">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('GatewayLogList')} 
                ]}/>

            <h3 className="mb-3">{t('GatewayLogList')}</h3>

            <FiltersCard>
                <Formik initialValues={activeFilters} onSubmit={handleFilterSearch} enableReinitialize>
                    {({resetForm}) => (
                        <Form>
                            <div className="row">
                                {/* <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('Id')}
                                        <Field className="form-control" name="logId"/>
                                    </label>
                                </div> */}
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('UserName')}
                                        <Field className="form-control" name="userName"/>
                                    </label>
                                </div> 
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('FullName')}
                                        <Field className="form-control" name="fullName"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('DateFrom')}
                                        <FormikDatePicker name="datefrom" showTime placeholderText={t('ChooseDate')}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('DateTo')}
                                        <FormikDatePicker name="dateto" showTime endOfDayDefault placeholderText={t('ChooseDate')}/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('CorrelationId')}
                                        <Field className="form-control" name="correlationid"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{ t('ClassName')}
                                        <Field className="form-control" name="classname"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('LogType')}
                                        <FormikSelect
                                            name="logType"
                                            options={logTypeOptions}
                                            isClearable
                                            clearValue={null}
                                            placeholder={`-- ${t('SelectOption')} --`}
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('AuditLevel')}
                                        <FormikSelect
                                            name="auditLevel"
                                            options={auditLevelOptions}
                                            isClearable
                                            clearValue={null}
                                            placeholder={`-- ${t('SelectOption')} --`}
                                        />
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3">
                                    <label className="form-label w-100">{t('UserId')}
                                        <Field className="form-control" name="userid"/>
                                    </label>
                                </div>
                                <div className="col-md-6 col-lg-4 col-xl-3 align-self-end">
                                    <button type="submit" className="btn gg-btn gg-btn-submit mb-2 me-2">
                                        { t('Search')}
                                    </button>
                                    <button type="button" className="btn btn-secondary text-white mb-2"
                                            onClick={() => {
                                                resetForm();
                                                setActiveFilters(initialFormState);
                                                handleFilterSearch(initialFormState);
                                            }}>
                                        { t('Reset')}
                                    </button>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </FiltersCard>

            {loading && (<Loader />)}

            <div className="table-responsive border p-1 rounded-2">
                <table className="table table-sm table-striped-columns small table-logs">
                    <thead>
                    <tr>
                        {/* <th style={{width: "30%"}} onClick={() => handleSort("logId")}>{t('Id')}
                            <SortDirection isActive={sortField === 'logId'} order={order}/>
                        </th> */}
                        <th style={{width: "30%"}} className="text-nowrap">{t('LogMessage')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('UserName')}</th>
                        <th style={{width: "30%"}} className="text-nowrap">{t('FullName')}</th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("ClassName")}>
                            { t('ClassName')}
                            <SortDirection isActive={sortField === 'ClassName'} order={order}/></th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("CorrelationId")}>{ t('CorrelationId')}
                            <SortDirection isActive={sortField === 'CorrelationId'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("LogType")}>{ t('LogType')}
                            <SortDirection isActive={sortField === 'LogType'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("AuditLevel")}>{ t('AuditLevel')}
                            <SortDirection isActive={sortField === 'AuditLevel'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("TraceEventType")}>{ t('TraceEventType')}
                            <SortDirection isActive={sortField === 'TraceEventType'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("CreatedAt")} >{ t('CreatedAt')}
                            <SortDirection isActive={sortField === 'CreatedAt'} order={order}/>
                        </th>
                        <th style={{width: "30%"}} className="text-nowrap" onClick={() => handleSort("UserId")}>{ t('UserId')}
                            <SortDirection isActive={sortField === 'UserId'} order={order}/>
                        </th> 
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {auditLogs.map(log => (
                        <tr key={log.logId} className={'table-logs__item '}>
                            {/* <td className="text-nowrap">
                                <NavLink to={'/gateway-logs/details/' + log.logId}>{log.logId}</NavLink>
                            </td> */}
                            <td className="text-nowrap">
                                <NavLink to={'/gateway-logs/details/' + log.logId}>{truncateText(log.logMessage, 20)}</NavLink>
                            </td>
                            <td className="text-nowrap">
                                {log.userName && log.userName.length > 0 ? 
                                    log.userName
                                    : <i className="text-danger">{t('UserRemoved')}</i>
                                }
                            </td>
                            <td className="text-nowrap">
                                {log.fullName && log.fullName.length > 0 && log.fullName}
                            </td>
                            <td className="text-nowrap">{log.className}</td>
                            <td className="text-nowrap">{log.correlationId}</td>
                            <td className="text-nowrap">{LogTypeEnumText[log.logType]}</td>
                            <td className="text-nowrap">{AuditLevelEnumText[log.auditLevel]}</td>
                            <td className="text-nowrap">{log.traceEventType}</td>
                            <td className="text-nowrap">{formatDateTime(log.createdAt)}</td>
                            <td className="text-nowrap">{log.userId}</td> 
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <Pagination currentPage={page}
                onPageChange={(page) => { setPage(page) }}
                onPageSizeChange={(size) => setPageSize(size)}
                totalItems={total} />
        </div>
    )
}
export default GatewayLogList;
